import { INotificationAdapter } from './INotificationAdapter';
import { ApiResponse } from '../../types/api';
import { Notification, NotificationFilter, NotificationEventType } from '../../types/notification';

function ok<T>(data: T): ApiResponse<T> {
  return { success: true, data, message: 'OK' };
}

const DEMO_NOTIFICATIONS: Notification[] = [
  {
    id: 'NTF-001',
    event_type: 'complaint_assigned',
    title: 'Complaint Assigned',
    message: 'Complaint CMP-2026-00001 has been assigned to you for review.',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    recipient_user_id: 'USR-00003',
    recipient_role: 'OFFICER',
    is_read: false,
    channel: 'in_app',
    created_at: '2026-09-12T08:30:00Z',
  },
  {
    id: 'NTF-002',
    event_type: 'work_completed',
    title: 'Work Completed',
    message: 'Work order WO-2026-00003 has been marked as completed by Ahmad Khan.',
    entity_type: 'work_order',
    entity_id: 'WO-2026-00003',
    recipient_user_id: 'USR-00004',
    recipient_role: 'SUPERVISOR',
    is_read: false,
    channel: 'in_app',
    created_at: '2026-09-12T09:15:00Z',
  },
  {
    id: 'NTF-003',
    event_type: 'sla_warning',
    title: 'SLA Warning',
    message: 'Complaint CMP-2026-00005 is approaching SLA deadline. 75% of resolution time elapsed.',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00005',
    recipient_user_id: 'USR-00003',
    recipient_role: 'OFFICER',
    is_read: true,
    channel: 'in_app',
    created_at: '2026-09-12T07:00:00Z',
  },
  {
    id: 'NTF-004',
    event_type: 'vendor_assigned',
    title: 'Vendor Assigned',
    message: 'Alpha Technical Services has been assigned to work order WO-2026-00001.',
    entity_type: 'work_order',
    entity_id: 'WO-2026-00001',
    recipient_user_id: 'USR-00005',
    recipient_role: 'VENDOR',
    is_read: false,
    channel: 'in_app',
    created_at: '2026-09-12T10:00:00Z',
  },
  {
    id: 'NTF-005',
    event_type: 'verification_required',
    title: 'Verification Required',
    message: 'Work order WO-2026-00004 requires your inspection and verification.',
    entity_type: 'work_order',
    entity_id: 'WO-2026-00004',
    recipient_user_id: 'USR-00004',
    recipient_role: 'SUPERVISOR',
    is_read: true,
    channel: 'in_app',
    created_at: '2026-09-12T06:30:00Z',
  },
  {
    id: 'NTF-006',
    event_type: 'new_comment',
    title: 'New Comment',
    message: 'Alex Mercer commented on complaint CMP-2026-00001.',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00001',
    recipient_user_id: 'USR-00003',
    recipient_role: 'OFFICER',
    is_read: true,
    channel: 'in_app',
    created_at: '2026-09-12T08:20:00Z',
  },
  {
    id: 'NTF-007',
    event_type: 'sla_breached',
    title: 'SLA Breached',
    message: 'Complaint CMP-2026-00008 has breached its SLA resolution deadline.',
    entity_type: 'complaint',
    entity_id: 'CMP-2026-00008',
    recipient_user_id: 'USR-00001',
    recipient_role: 'ADMIN',
    is_read: false,
    channel: 'in_app',
    created_at: '2026-09-12T05:00:00Z',
  },
];

export class GASNotificationAdapter implements INotificationAdapter {
  private notifications: Notification[] = [...DEMO_NOTIFICATIONS];

  async getNotifications(filter?: NotificationFilter): Promise<ApiResponse<Notification[]>> {
    await new Promise(r => setTimeout(r, 300));
    let result = [...this.notifications];
    if (filter?.recipient_user_id) result = result.filter(n => n.recipient_user_id === filter.recipient_user_id);
    if (filter?.is_read !== undefined) result = result.filter(n => n.is_read === filter.is_read);
    if (filter?.event_type) result = result.filter(n => n.event_type === filter.event_type);
    if (filter?.entity_type) result = result.filter(n => n.entity_type === filter.entity_type);
    result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return ok(result);
  }

  async getUnreadCount(userId: string): Promise<ApiResponse<number>> {
    await new Promise(r => setTimeout(r, 200));
    const count = this.notifications.filter(n => n.recipient_user_id === userId && !n.is_read).length;
    return ok(count);
  }

  async markAsRead(notificationId: string): Promise<ApiResponse<void>> {
    await new Promise(r => setTimeout(r, 200));
    const idx = this.notifications.findIndex(n => n.id === notificationId);
    if (idx !== -1) this.notifications[idx] = { ...this.notifications[idx], is_read: true };
    return ok(undefined as unknown as void);
  }

  async markAllRead(userId: string): Promise<ApiResponse<void>> {
    await new Promise(r => setTimeout(r, 300));
    this.notifications = this.notifications.map(n =>
      n.recipient_user_id === userId ? { ...n, is_read: true } : n
    );
    return ok(undefined as unknown as void);
  }
}
